// import { resetPriceRecalculationResponse } from '../../../ordersummary/actions';

import React, { useState } from 'react';
import {
  getMultiSelectQuantityDetails,
  getQuantityValidation,
  makeGetChildOptionsSelector,
} from '../../../../bom/selectors';
import { useDispatch, useSelector } from 'react-redux';

import OptionQuantity from '../OptionQuantity';
import PropTypes from 'prop-types';
import deepEqual from 'deep-equal';
import { selectLeafMultipleSelectionOption } from '../../../../bom/actions';
import styles from './ChildOptionQuantityEditor.scss';

const ChildOptionQuantityEditor = React.forwardRef(
  ({ childQuestion, closePreviousSelectionNotAvailableWarning, feedback, parentOption, resetFeedback }, ref) => {
    const dispatch = useDispatch();

    const getChildOptions = makeGetChildOptionsSelector(childQuestion.code, parentOption.codeValue);
    const childOptions = useSelector(getChildOptions);
    const quantityValidation = useSelector(getQuantityValidation);

    const multiSelectQuantityDetails = useSelector(getMultiSelectQuantityDetails);
    const allQuantitiesEntered =
      Object.values(multiSelectQuantityDetails).length > 0 &&
      Object.values(multiSelectQuantityDetails).every((qty) => qty > 0);

    const [quantities, setQuantities] = useState(
      Object.fromEntries(childOptions.map((option) => [option.codeValue, option.quantity]))
    );

    const handleQuantityChanged = (key, qty) => {
      setQuantities({ ...quantities, [key]: qty });
      if (
        !!closePreviousSelectionNotAvailableWarning &&
        typeof closePreviousSelectionNotAvailableWarning === 'function'
      ) {
        closePreviousSelectionNotAvailableWarning();
      }
      resetFeedback('quantity');
    };

    const handleSaveQuantity = () => {
      const lastQuantities = Object.fromEntries(childOptions.map((option) => [option.codeValue, option.quantity]));
      if (!deepEqual(quantities, lastQuantities)) {
        // Note: comment out these statements to fix the issue of displaying 'NA' in the Order Summary section
        // const sumReducer = (total, currentValue) => total + currentValue;
        // const newTotalQuantity = Object.values(quantities).reduce(sumReducer, 0);
        // const oldTotalQuantity = childOptions.map((option) => option.quantity).reduce(sumReducer, 0);
        // if (newTotalQuantity !== oldTotalQuantity) {
        //   dispatch(resetPriceRecalculationResponse());
        // }
        dispatch(selectLeafMultipleSelectionOption(childQuestion.code, parentOption.codeValue, quantities));
      }
    };

    return (
      <div className={styles['child-option-quantity-editor']}>
        {childOptions.map((option, index) => (
          <OptionQuantity
            key={`${parentOption.codeValue}-${option.codeValue}`}
            isInvalid={
              !!feedback['quantity'] &&
              !quantityValidation.isValid &&
              allQuantitiesEntered &&
              quantities[option.codeValue] > 0
            }
            option={option}
            parentOption={parentOption}
            quantity={quantities[option.codeValue]}
            ref={index === 0 ? ref : undefined}
            onQuantityChanged={handleQuantityChanged}
            onSaveQuantity={handleSaveQuantity}
          />
        ))}
      </div>
    );
  }
);

ChildOptionQuantityEditor.propTypes = {
  childQuestion: PropTypes.shape({
    code: PropTypes.string.isRequired,
    isRealParentChildQuestion: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    mandatoryFlag: PropTypes.string.isRequired,
    needToReloadUniqueOptions: PropTypes.bool.isRequired,
    questionId: PropTypes.number.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    representationType: PropTypes.oneOf([0, 1]).isRequired,
    sampleQuestionText: PropTypes.string,
    selectionType: PropTypes.oneOf([0, 1]).isRequired,
    sequence: PropTypes.number.isRequired,
    thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  }).isRequired,
  closePreviousSelectionNotAvailableWarning: PropTypes.func,
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  parentOption: PropTypes.shape({
    code: PropTypes.string.isRequired,
    codeValue: PropTypes.string.isRequired,
    colorGroupList: PropTypes.arrayOf(PropTypes.string).isRequired,
    colorReference: PropTypes.string.isRequired,
    componentId: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    estimatedShipDate: PropTypes.string.isRequired,
    futureShipDateSettingStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
    helpLink: PropTypes.shape({
      helpText: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
    }),
    inventoryMessage: PropTypes.string.isRequired,
    isFutureShipDateSettable: PropTypes.bool.isRequired,
    isOutOfStock: PropTypes.bool.isRequired,
    isSelected: PropTypes.bool.isRequired,
    isSoldOut: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    optionText: PropTypes.string.isRequired,
    outOfStockMessage: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    sequence: PropTypes.number.isRequired,
    specialBomCharge: PropTypes.string.isRequired,
    useTexture: PropTypes.bool.isRequired,
    yearOptionPricing: PropTypes.oneOf([0, 1, 2]).isRequired,
  }).isRequired,
  resetFeedback: PropTypes.func.isRequired,
};

export default ChildOptionQuantityEditor;
